// src/components/roads/RoadSegmentLayer.js
// --- UPDATE (GIS map — road segment health layer) -----------------------------
// react-leaflet layer (render inside <MapContainer>) that draws every road segment coloured by
// its health (PCI band) and explains a segment when it is clicked.
//
//   <RoadSegmentLayer data={{ roads, segments, unsegmented }} visibleBands={Set} showLabels
//        selectedSegmentId onSegmentSelect(seg, road) onOpenRoadReport(road) />
//
//   • Canvas renderer — thousands of segments stay smooth; hover thickens the segment.
//   • Tooltip on hover: road, segment, PCI + band, distress and the FlowSense treatment.
//   • Click: popup with every detail (station range, length, PCI, potholes, cracking, rutting,
//     structural remaining life, Drive Mode AI detections, last inspected, open work orders on the
//     road) + "Open Works Report" and "Zoom to segment".
//   • PCI badges on each segment from zoom 16 (or always, up to 300 segments, when showLabels).
//   • Roads that are mapped but not sliced yet are drawn dashed grey ("not sliced yet").
// --- END UPDATE ---------------------------------------------------------------
// --- UPDATE (GIS map — whole network) ----------------------------------------
//   • Built for the whole network: every segment is a plain Leaflet canvas polyline in ONE layer
//     group (no React element, tooltip or popup object per segment). Tooltips are created on first
//     hover; one shared popup (React) opens on click. PCI badges are drawn only for segments in view.
// --- END UPDATE ---------------------------------------------------------------
// --- UPDATE (roads v1.3): the segment popup shows TessaDEM ground (start → end, grade) and the latest road ponding
// forecast (depth, when it starts, how long it stays) when the segment map API sends them (seg.ground / seg.ponding).
// --- END UPDATE ---------------------------------------------------------------
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Popup, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import {
    bandOf, treatmentFor, segmentPositions, midpointOf, filterSegments, polylineLengthM, describeSegment,
} from './roadSegmentHealth';

const LABEL_ZOOM = 16;
const MAX_LABELS = 300;

const labelIcons = new Map();
/** Small PCI badge drawn on the segment (divIcon cached per value + colour). */
const labelIcon = (pci, color) => {
    const key = `${pci}|${color}`;
    if (!labelIcons.has(key)) {
        labelIcons.set(key, L.divIcon({
            className: 'fs-segment-label',
            iconSize: [30, 18],
            iconAnchor: [15, 9],
            html: `<div style="background:rgba(255,255,255,0.95);border:2px solid ${color};border-radius:9px;font:800 10px/14px system-ui,sans-serif;color:#1A202C;text-align:center;box-shadow:0 1px 3px rgba(0,0,0,.4)">${pci ?? '—'}</div>`,
        }));
    }
    return labelIcons.get(key);
};

const fmt = (v, unit = '') => (v === null || v === undefined || v === '' ? '—' : `${v}${unit}`);
const fmtDate = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};
const human = (v) => (v ? String(v).replace(/_/g, ' ') : '—');

const row = (label, value, color) => (
    <tr key={label}>
        <td style={{ color: '#718096', padding: '2px 8px 2px 0', whiteSpace: 'nowrap' }}>{label}</td>
        <td style={{ fontWeight: 600, color: color || '#1A202C', textAlign: 'right' }}>{value}</td>
    </tr>
);

/** Popup body for one segment. */
export function SegmentDetails({ seg, road, onOpenRoadReport, onZoom }) {
    const band = bandOf(seg.pci);
    const t = treatmentFor(seg.pci);
    const drive = seg.drive;
    return (
        <div style={{ minWidth: 240, maxWidth: 290, fontSize: 12, lineHeight: 1.4 }} data-testid={`segment-popup-${seg.id}`}>
            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 2 }}>{road?.name || 'Road'}</div>
            <div style={{ color: '#718096', fontSize: 11, marginBottom: 8 }}>
                Segment {seg.index ?? '—'} · <span style={{ fontFamily: 'monospace' }}>{seg.id}</span>{road?.road_code ? ` · ${road.road_code}` : ''}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: `${band.color}22`, border: `1px solid ${band.color}`, marginBottom: 8 }}>
                <div style={{ fontSize: 26, fontWeight: 900, color: band.color, lineHeight: 1 }}>{seg.pci ?? '—'}</div>
                <div>
                    <div style={{ fontWeight: 800 }}>{band.label}{seg.pci !== null && seg.pci !== undefined ? ' · PCI' : ''}</div>
                    <div style={{ color: '#4A5568', fontSize: 11 }}>{t ? t.label : 'Run the ML evaluation in the Works Report to score it.'}</div>
                </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                    {row('Station', seg.station_start_m !== null && seg.station_end_m !== null ? `${Math.round(seg.station_start_m)}–${Math.round(seg.station_end_m)} m` : '—')}
                    {row('Length', fmt(seg.length_m !== null && seg.length_m !== undefined ? Math.round(seg.length_m) : null, ' m'))}
                    {row('Potholes', fmt(seg.potholes), seg.potholes > 0 ? '#C53030' : undefined)}
                    {row('Cracking', human(seg.cracking), seg.cracking && !['none', 'low'].includes(String(seg.cracking)) ? '#C05621' : undefined)}
                    {row('Rutting', fmt(seg.rutting_mm, ' mm'), seg.rutting_mm > 12 ? '#C05621' : undefined)}
                    {row('Remaining life', seg.rul_years !== null && seg.rul_years !== undefined ? `${seg.rul_years} yr` : '—')}
                    {drive && row('Drive AI detections', drive.findings !== null ? `${drive.findings}${drive.confirmed !== null ? ` (${drive.confirmed} confirmed)` : ''}` : '—')}
                    {drive?.potholes ? row('Drive AI potholes', drive.potholes, '#C53030') : null}
                    {/* --- UPDATE (roads v1.3) */}
                    {seg.ground && (seg.ground.start_m !== null || seg.ground.end_m !== null) && row('Ground (TessaDEM)', `${fmt(seg.ground.start_m)} → ${fmt(seg.ground.end_m)} m${seg.ground.slope_pct !== null && seg.ground.slope_pct !== undefined ? ` · ${seg.ground.slope_pct}%` : ''}`)}
                    {seg.ponding && row('Rain water (forecast)', `${seg.ponding.depth_m !== null ? `${Math.round(seg.ponding.depth_m * 100)} cm` : '—'} · ${seg.ponding.band}${seg.ponding.starts_hours !== null ? ` · from +${Math.round(seg.ponding.starts_hours)} h` : ''}${seg.ponding.clears_hours !== null ? ` · drains ${Math.round(seg.ponding.clears_hours)} h after` : ' · stays after the rain'}`, seg.ponding.band === 'severe' ? '#553C9A' : '#2B6CB0')}
                    {/* --- END UPDATE */}
                    {row('Last inspected', fmtDate(seg.last_inspected_at))}
                    {row('Health updated', fmtDate(seg.drive?.at || seg.updated_at))}
                    {road && row('Open work orders (road)', road.open_work_orders ? `${road.open_work_orders}${road.critical_work_orders ? ` · ${road.critical_work_orders} critical` : ''}` : 'None', road.critical_work_orders ? '#C53030' : undefined)}
                </tbody>
            </table>
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                {onOpenRoadReport && road && (
                    <button type="button" onClick={() => onOpenRoadReport(road, seg)} data-testid="segment-open-report"
                        style={{ flex: 1, padding: '6px 8px', background: '#3182CE', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
                        Open Works Report
                    </button>
                )}
                {onZoom && (
                    <button type="button" onClick={onZoom}
                        style={{ padding: '6px 8px', background: '#EDF2F7', color: '#2D3748', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
                        Zoom
                    </button>
                )}
            </div>
        </div>
    );
}
SegmentDetails.propTypes = { seg: PropTypes.object.isRequired, road: PropTypes.object, onOpenRoadReport: PropTypes.func, onZoom: PropTypes.func };

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function RoadSegmentLayer({ data, visibleBands, showLabels = false, selectedSegmentId, onSegmentSelect, onOpenRoadReport, showUnsegmented = true }) {
    const map = useMap();
    const [view, setView] = useState(() => ({ zoom: map.getZoom(), bounds: map.getBounds() }));
    useMapEvents({ moveend: () => setView({ zoom: map.getZoom(), bounds: map.getBounds() }) });
    const renderer = useMemo(() => L.canvas({ padding: 0.5, tolerance: 6 }), []);
    const [active, setActive] = useState(null); // { seg, road, latlng } — the one open popup

    const roadsById = useMemo(() => {
        const m = {};
        (data?.roads || []).forEach((r) => {
            m[String(r.id)] = { ...r, _lengthM: Array.isArray(r.polyline) && r.polyline.length > 1 ? polylineLengthM(r.polyline) : 0 };
        });
        (data?.segments || []).forEach((s) => {
            const r = m[String(s.road_id)];
            if (r && s.station_end_m > (r._maxStation || 0)) r._maxStation = s.station_end_m;
        });
        return m;
    }, [data]);

    const drawn = useMemo(() => filterSegments(data?.segments || [], visibleBands)
        .map((s) => ({ seg: s, road: roadsById[String(s.road_id)] || null }))
        .map((x) => ({ ...x, positions: segmentPositions(x.seg, x.road) }))
        .filter((x) => x.positions.length >= 2), [data, visibleBands, roadsById]);

    // ── one imperative canvas layer for every segment (React elements per segment do not scale to
    //    a whole network of tens of thousands of segments) ──────────────────────────────────────
    const lineByIdRef = useRef(new Map());
    const handlersRef = useRef({});
    handlersRef.current = { onSegmentSelect };
    useEffect(() => {
        const group = L.layerGroup();
        const lines = new Map();
        if (showUnsegmented) {
            (data?.unsegmented || []).forEach((rid) => {
                const r = roadsById[String(rid)];
                if (!r || !Array.isArray(r.polyline) || r.polyline.length < 2) return;
                const pl = L.polyline(r.polyline.map((p) => [p.lat, p.lng]), { renderer, color: '#A0AEC0', weight: 4, opacity: 0.7, dashArray: '2 8' });
                pl.bindTooltip(`<div style="font-weight:700">${esc(r.name)}</div><div style="font-size:12px">Not sliced into segments yet — open its Works Report to slice and score it.</div>`, { sticky: true, direction: 'top' });
                group.addLayer(pl);
            });
        }
        drawn.forEach(({ seg, road, positions }) => {
            const band = bandOf(seg.pci);
            const pl = L.polyline(positions, {
                renderer, color: band.color, weight: 6, opacity: 0.95, lineCap: 'butt',
                dashArray: band.key === 'unscored' ? '6 6' : undefined,
            });
            pl._fsSeg = seg; pl._fsRoad = road;
            pl.on('mouseover', (e) => {
                if (!pl.getTooltip()) {
                    pl.bindTooltip(`<div style="font-weight:700">${esc(road?.name || 'Road')} · segment ${esc(seg.index ?? '—')}</div><div style="font-size:12px">${esc(describeSegment(seg))}</div>`, { sticky: true, direction: 'top', opacity: 0.95 });
                    pl.openTooltip(e.latlng);
                }
                pl.setStyle({ weight: (pl._fsSelected ? 9 : 6) + 3, opacity: 1 });
            });
            pl.on('mouseout', () => pl.setStyle({ weight: pl._fsSelected ? 9 : 6, opacity: 0.95 }));
            pl.on('click', (e) => {
                L.DomEvent.stopPropagation(e);
                setActive({ seg, road, latlng: e.latlng });
                handlersRef.current.onSegmentSelect?.(seg, road);
            });
            lines.set(String(seg.id), pl);
            group.addLayer(pl);
        });
        group.addTo(map);
        lineByIdRef.current = lines;
        return () => { group.remove(); lineByIdRef.current = new Map(); };
    }, [map, renderer, drawn, data, roadsById, showUnsegmented]);

    // selected segment = thicker line
    useEffect(() => {
        const pl = selectedSegmentId != null ? lineByIdRef.current.get(String(selectedSegmentId)) : null;
        if (!pl) return undefined;
        pl._fsSelected = true;
        pl.setStyle({ weight: 9 });
        pl.bringToFront();
        return () => { pl._fsSelected = false; pl.setStyle({ weight: 6 }); };
    }, [selectedSegmentId, drawn]);

    // the open popup follows a data refresh (same segment id, fresh numbers) or closes if it is filtered out
    const activeLive = useMemo(() => {
        if (!active) return null;
        const hit = drawn.find((x) => String(x.seg.id) === String(active.seg.id));
        return hit ? { ...active, seg: hit.seg, road: hit.road, positions: hit.positions } : null;
    }, [active, drawn]);
    const zoomToActive = useCallback(() => {
        if (!activeLive?.positions) return;
        try { map.fitBounds(L.latLngBounds(activeLive.positions), { padding: [60, 60], maxZoom: 19 }); } catch { /* invalid bounds */ }
    }, [map, activeLive]);

    // PCI badges only for segments in view (never more than MAX_LABELS DOM elements)
    const labels = useMemo(() => {
        if (!(view.zoom >= LABEL_ZOOM || showLabels)) return [];
        const out = [];
        for (const x of drawn) {
            const mid = midpointOf(x.positions);
            if (mid && view.bounds.contains(mid)) {
                out.push({ id: x.seg.id, mid, pci: x.seg.pci, color: bandOf(x.seg.pci).color });
                if (out.length > MAX_LABELS) return [];
            }
        }
        return out;
    }, [drawn, view, showLabels]);

    return (
        <>
            {labels.map((l) => (
                <Marker key={`lbl-${l.id}`} position={l.mid} icon={labelIcon(l.pci, l.color)} interactive={false} keyboard={false} title={`PCI ${l.pci ?? '—'}`} />
            ))}
            {activeLive && (
                <Popup position={activeLive.latlng} maxWidth={320} autoPan eventHandlers={{ remove: () => setActive(null) }}>
                    <SegmentDetails seg={activeLive.seg} road={activeLive.road} onOpenRoadReport={onOpenRoadReport} onZoom={zoomToActive} />
                </Popup>
            )}
        </>
    );
}

RoadSegmentLayer.propTypes = {
    data: PropTypes.shape({ roads: PropTypes.array, segments: PropTypes.array, unsegmented: PropTypes.array }),
    visibleBands: PropTypes.oneOfType([PropTypes.instanceOf(Set), PropTypes.array]),
    showLabels: PropTypes.bool,
    selectedSegmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSegmentSelect: PropTypes.func,
    onOpenRoadReport: PropTypes.func,
    showUnsegmented: PropTypes.bool,
};

export default memo(RoadSegmentLayer);
